interface StoreSchemaItem {
    name: string;
    keypath: string;
    options?: IDBIndexParameters;
}

interface StoreMeta {
    store: string;
    storeConfig: {
        keyPath: string;
        autoIncrement?: boolean;
    };
    storeSchema?: StoreSchemaItem[];
}

interface IndexedDbConfig {
    name: string;
    version: number;
    objectStoresMeta: StoreMeta[];
}

let currentConfig: IndexedDbConfig | null = null;

const ensureStores = (
    db: IDBDatabase,
    config: IndexedDbConfig,
    upgradeTx?: IDBTransaction | null
) => {
    config.objectStoresMeta.forEach((storeMeta) => {
        let objectStore: IDBObjectStore;
        if (!db.objectStoreNames.contains(storeMeta.store)) {
            objectStore = db.createObjectStore(
                storeMeta.store,
                storeMeta.storeConfig
            );
        } else {
            if (!upgradeTx) {
                return;
            }
            objectStore = upgradeTx.objectStore(storeMeta.store);
        }

        storeMeta.storeSchema?.forEach((schema) => {
            if (!objectStore.indexNames.contains(schema.name)) {
                objectStore.createIndex(
                    schema.name,
                    schema.keypath,
                    schema.options
                );
            }
        });
    });
};

const openDatabase = (
    config: IndexedDbConfig = currentConfig as IndexedDbConfig
): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
        if (!config) {
            reject(new Error("IndexedDB is not initialized"));
            return;
        }

        const request = indexedDB.open(config.name, config.version);

        request.onupgradeneeded = () => {
            const db = request.result;
            ensureStores(db, config, request.transaction);
        };

        request.onsuccess = () => {
            resolve(request.result);
        };

        request.onerror = () => {
            reject(request.error);
        };
    });

const runStoreOperation = <T>(
    storeName: string,
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest
): Promise<T> =>
    openDatabase().then(
        (db) =>
            new Promise<T>((resolve, reject) => {
                const tx = db.transaction(storeName, mode);
                const store = tx.objectStore(storeName);
                const request = operation(store);

                request.onsuccess = () => {
                    resolve(request.result as T);
                };

                request.onerror = () => {
                    reject(request.error);
                };

                tx.oncomplete = () => {
                    db.close();
                };

                tx.onerror = () => {
                    reject(tx.error);
                };
            })
    );

export const initDB = (config: IndexedDbConfig) => {
    currentConfig = config;
    openDatabase(config)
        .then((db) => db.close())
        .catch((error) => {
            console.error("IndexedDB init error", error);
        });
};

export const useIndexedDB = (storeName: string) => {
    return {
        add: (value: unknown) =>
            runStoreOperation<IDBValidKey>(storeName, "readwrite", (store) =>
                store.add(value)
            ),
        update: (value: unknown) =>
            runStoreOperation<IDBValidKey>(storeName, "readwrite", (store) =>
                store.put(value)
            ),
        deleteRecord: (key: IDBValidKey) =>
            runStoreOperation<undefined>(storeName, "readwrite", (store) =>
                store.delete(key)
            ),
        getByIndex: (indexName: string, queryValue: unknown) =>
            runStoreOperation<any>(storeName, "readonly", (store) =>
                store.index(indexName).get(queryValue as IDBValidKey)
            ),
    };
};
